import React from 'react'
import { Panel } from 'primereact/panel'

import { ChargePoint } from '../../redux/api/web-api'
import findChargingConnector from '../../../lib/findChargingConnector'
import findSchukoConnector from '../../../lib/findSchukoConnector'
import ConnectorStatus from '../../../lib/connector-status'
import isHalo from '../../../lib/isHalo'
import isAura from '../../../lib/isAura'
import isLuna from '../../../lib/isLuna'
import isDawn from '../../../lib/isDawn'
import isSchukoEnabled from '../../../lib/isSchukoEnabled'
import Text from '../../../ui/Text'
import isOnline from '../../../lib/isOnline'

export type ChargePointStatusWidgetProps = {
    chargePoint: ChargePoint
    nameInTitle?: boolean
}

export function ChargePointStatusWidget({
    chargePoint,
    nameInTitle,
}: ChargePointStatusWidgetProps): React.ReactElement {
    const chargingConnector = findChargingConnector(chargePoint?.connectors ?? [])
    const socketConnector = findSchukoConnector(chargePoint?.connectors ?? [])
    const showSocketStatus = isHalo(chargePoint) && isSchukoEnabled(chargePoint) && Boolean(socketConnector)

    const chargerIsOnline = isOnline(chargePoint)

    return (
        <Panel
            toggleable
            header={
                <div className="flex">
                    <span>
                        {nameInTitle ? chargePoint.name : ''} <Text code="lblStatus" />
                    </span>
                </div>
            }
            data-testid="charge-point-status-widget"
        >
            {(isHalo(chargePoint) || isDawn(chargePoint) || isLuna(chargePoint)) && (
                <div className={'flex flex-column ' + (showSocketStatus ? 'mb-4' : 'mb-0')}>
                    <div className="text-700" data-testid="charger-status-section">
                        <Text code="lblCharger" />
                    </div>
                    <div data-testid="mobile-value" className="mt-2">
                        {chargerIsOnline ? (
                            <ConnectorStatus
                                connector={chargingConnector}
                                connectorTopLimitation={chargingConnector?.topLimitingFactor}
                                isOcpp={chargePoint?.isOcpp}
                                showConsumption={true}
                                showErrorStatus={true}
                                productId={chargePoint?.productId}
                                labeled={true}
                            />
                        ) : (
                            <Text code="lblOffline" />
                        )}
                    </div>
                </div>
            )}

            {showSocketStatus && (
                <div className="flex flex-column">
                    <div className="text-700" data-testid="socket-status-section">
                        <Text code="lblSocket" />
                    </div>
                    <div data-testid="mobile-value" className="mt-2">
                        {chargerIsOnline ? (
                            <ConnectorStatus
                                connector={socketConnector}
                                isOcpp={chargePoint?.isOcpp}
                                showConsumption={true}
                                showErrorStatus={true}
                                productId={chargePoint?.productId}
                                labeled={true}
                            />
                        ) : (
                            <Text code="lblOffline" />
                        )}
                    </div>
                </div>
            )}

            {isAura(chargePoint) &&
                chargePoint?.connectors?.map((connector, index) => {
                    return (
                        <div
                            key={`aura-${connector.connectorId}-${connector.name ?? 'connector'}-${index}`}
                            className={'flex flex-column ' + (connector.connectorId === 1 ? 'mb-4' : 'mb-0')}
                        >
                            <div className="text-700" data-testid="aura-socket-status-section">
                                <Text code={connector.connectorId === 1 ? 'lblLeftSocket' : 'lblRightSocket'} />
                            </div>
                            <div data-testid="mobile-value" className="mt-2">
                                {chargerIsOnline ? (
                                    <ConnectorStatus
                                        connectorTopLimitation={connector.topLimitingFactor}
                                        connector={connector}
                                        isOcpp={chargePoint?.isOcpp}
                                        showConsumption={true}
                                        showErrorStatus={true}
                                        productId={chargePoint?.productId}
                                        labeled={true}
                                    />
                                ) : (
                                    <Text code="lblOffline" />
                                )}
                            </div>
                        </div>
                    )
                })}
        </Panel>
    )
}
